/**
 * AuthMedia – henter en autorisert media-URL og viser via blob.
 * Brukes for `/api/collab-media/raw?path=...` som krever Bearer-token.
 */
import React, { useEffect, useRef, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

function extractRelative(url) {
  // Konverter "https://.../api/collab-media/raw?..." → "/collab-media/raw?..."
  // som api.get fungerer med (baseURL satt til /api).
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.replace(/^\/api/, "") + u.search;
  } catch {
    return url.replace(/^.*\/api/, "");
  }
}

export function AuthImage({ url, alt = "", className = "" }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const lastBlob = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setBlobUrl(null);
    api.get(extractRelative(url), { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const u = URL.createObjectURL(r.data);
        if (lastBlob.current) URL.revokeObjectURL(lastBlob.current);
        lastBlob.current = u;
        setBlobUrl(u);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (lastBlob.current) URL.revokeObjectURL(lastBlob.current);
      lastBlob.current = null;
    };
  }, [url]);

  if (failed) {
    return <div className={`flex items-center justify-center bg-black/30 text-[var(--cream-faint)] text-xs ${className}`}>!</div>;
  }
  if (!blobUrl) {
    return <div className={`bg-black/30 animate-pulse ${className}`} />;
  }
  return <img src={blobUrl} alt={alt} className={className} />;
}

export async function authDownload(url, filename) {
  try {
    const r = await api.get(extractRelative(url), { responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "media";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (e) {
    toast.error("Kunne ikke laste ned filen.");
  }
}

export async function authView(url) {
  try {
    const r = await api.get(extractRelative(url), { responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    toast.error("Kunne ikke åpne filen.");
  }
}

export function AuthDownloadLink({ url, filename, className = "", children }) {
  return (
    <button type="button" onClick={() => authDownload(url, filename)} className={className}>
      {children || <Download className="h-5 w-5 text-[var(--magenta)]" />}
    </button>
  );
}
